import type {
	FilterRuleModel,
	ProductsByPCModel,
	ScenarioModel,
} from '@/shared/generated'

import { any, find, prop, propEq } from 'lodash/fp'

import { useAssortmentListQuery } from '@/entities/assortment'
import { usePreproQuery, useSettingsSchemasQuery } from '@/entities/core'
import {
	useGlobalPcSetQuery,
	useProductsCountsByPcQuery,
} from '@/entities/pricing-campaign'
import { useScenariosQuery } from '@/entities/scenario'

import { SettingsTemplateType } from '@/shared/generated'
import { getResolvedSchema } from '@/shared/tools/json-schema-utils'

export const usePricingCampaignData = ({
	optimizationGroupId,
	globalSetId,
	pricingCampaignId,
	draft,
}: {
	optimizationGroupId?: string | null
	globalSetId?: string
	pricingCampaignId?: string
	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
}) => {
	/**
	 * Queries
	 */
	const prepro = usePreproQuery()
	const mainScenarioQuery = useScenariosQuery<ScenarioModel | undefined>(
		optimizationGroupId!,
		{
			select: find<ScenarioModel>({ is_main: true }),
			enabled: !!optimizationGroupId,
		}
	)
	const isMainGlobalPcSetQuery = useGlobalPcSetQuery<boolean>(globalSetId!, {
		select: propEq('is_main', true),
		enabled: !!globalSetId,
	})
	const schemaQuery = useSettingsSchemasQuery({
		select: getResolvedSchema(`entity:${SettingsTemplateType.PricingCampaign}`),
	})
	const productsCountbyPcQuery = useProductsCountsByPcQuery<
		ProductsByPCModel[]
	>(
		{
			filters: [
				{
					name: 'pricing_campaign_id',
					operation: 'is',
					value: pricingCampaignId!,
				},
			],
		},
		{
			enabled: !!pricingCampaignId,
		}
	)
	const filtersProductsCountQuery = useAssortmentListQuery<number | undefined>(
		{
			limit: 1,
			filters: optimizationGroupId
				? [
						{
							name: 'optimization_group_id',
							operation: 'is',
							value: optimizationGroupId,
						},
						...(draft?.assignment_filters || []),
					]
				: draft?.assignment_filters,
		},
		{
			select: prop('total_count'),
			enabled: !!draft?.assignment_filters?.length,
		}
	)

	const isLoading = any(prop('isLoading'), [
		prepro,
		mainScenarioQuery,
		isMainGlobalPcSetQuery,
		schemaQuery,
		productsCountbyPcQuery,
		filtersProductsCountQuery,
	])

	return {
		mainScenarioId: mainScenarioQuery.data?.id,
		isMainGlobalPcSet: isMainGlobalPcSetQuery.data,
		schema: schemaQuery.data,
		productsCountbyPc: productsCountbyPcQuery.data,
		filtersProductsCount: filtersProductsCountQuery.data,
		isLoading,
	}
}
